/**
 * 管理员后台 API
 */
import api from './api';
import { AdminStats, AdminUserItem, AdminUserDetail, AdminOrderItem, AdminOrderDetail, PaginatedData, PlanInfo } from '../types';

export interface SystemModelInfo {
  id: number;
  name: string;
  provider: string;
  model_name: string;
  base_url?: string | null;
  is_active: boolean;
  is_default: boolean;
  sort_order: number;
  max_concurrent_requests: number;
  description?: string | null;
  created_at: string;
  updated_at: string;
  visibility?: string;
  visible_users?: number[];
  display_name?: string | null;
}

export interface GetUsersParams {
  page?: number;
  page_size?: number;
  search?: string;
  subscription_status?: string;
}

export interface GetOrdersParams {
  page?: number;
  page_size?: number;
  status?: string;
  username?: string;
  start_date?: string;
  end_date?: string;
}

export const adminApi = {
  getStats: () =>
    api.get<AdminStats>('/api/admin/stats/overview'),

  getUsers: (params?: GetUsersParams) =>
    api.get<PaginatedData<AdminUserItem>>('/api/admin/users', { params }),

  getUser: (id: number) =>
    api.get<AdminUserDetail>(`/api/admin/users/${id}`),

  updateUser: (id: number, data: { new_password?: string; is_active?: boolean }) =>
    api.put(`/api/admin/users/${id}`, data),

  updateSubscription: (id: number, data: { subscription_expiry: string | null }) =>
    api.put(`/api/admin/users/${id}/subscription`, data),

  getOrders: (params?: GetOrdersParams) =>
    api.get<PaginatedData<AdminOrderItem>>('/api/admin/orders', { params }),

  getOrder: (id: number) =>
    api.get<AdminOrderDetail>(`/api/admin/orders/${id}`),

  refundOrder: (id: number) =>
    api.post(`/api/admin/orders/${id}/refund`),

  // 套餐管理
  getPlans: () =>
    api.get<PlanInfo[]>('/api/admin/plans'),

  createPlan: (data: { plan_id: string; name: string; price: number; duration_days: number; description?: string; is_active?: boolean; sort_order?: number; max_per_user?: number | null }) =>
    api.post<PlanInfo>('/api/admin/plans', data),

  updatePlan: (id: number, data: { plan_id?: string; name?: string; price?: number; duration_days?: number; description?: string; is_active?: boolean; sort_order?: number; max_per_user?: number | null }) =>
    api.put<PlanInfo>(`/api/admin/plans/${id}`, data),

  deletePlan: (id: number) =>
    api.delete(`/api/admin/plans/${id}`),

  // 系统模型管理
  getSystemModels: () =>
    api.get<SystemModelInfo[]>('/api/admin/models'),

  createSystemModel: (data: { name: string; provider: string; model_name: string; base_url?: string; api_key: string; is_active?: boolean; is_default?: boolean; sort_order?: number; max_concurrent_requests?: number; description?: string }) =>
    api.post<SystemModelInfo>('/api/admin/models', data),

  updateSystemModel: (id: number, data: { name?: string; provider?: string; model_name?: string; base_url?: string; api_key?: string; is_active?: boolean; is_default?: boolean; sort_order?: number; max_concurrent_requests?: number; description?: string }) =>
    api.put<SystemModelInfo>(`/api/admin/models/${id}`, data),

  deleteSystemModel: (id: number) =>
    api.delete(`/api/admin/models/${id}`),

  updateModelVisibility: (id: number, data: { visibility: string; visible_users?: number[] }) =>
    api.put<SystemModelInfo>(`/api/admin/models/${id}/visibility`, data),

  // 提示词管理
  getPrompts: (documentType: 'bid' | 'proposal') =>
    api.get<any[]>(`/api/admin/prompts?document_type=${documentType}`),

  getPromptVersions: (key: string) =>
    api.get<any[]>(`/api/admin/prompts/${key}/versions`),

  updatePrompt: (key: string, data: { content_system: string; content_user: string | null }) =>
    api.put(`/api/admin/prompts/${key}`, data),

  revertPrompt: (key: string, version: number) =>
    api.post(`/api/admin/prompts/${key}/revert/${version}`),

  // 系统设置
  getSettings: () =>
    api.get<Record<string, string>>('/api/admin/settings'),

  updateSettings: (data: Record<string, string>) =>
    api.put('/api/admin/settings', data),

  // Skill 管理
  getSkills: () => api.get('/api/admin/skills'),
  createSkill: (data: any) => api.post('/api/admin/skills', data),
  installSkill: (data: { url?: string; json_content?: string; category?: string }) => api.post('/api/admin/skills/install', data),
  trainSkill: (formData: FormData) => api.post('/api/admin/skills/train', formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 }),
  getProjects: (params?: any) => api.get('/api/projects', { params: { page_size: 50, ...params } }),
  updateSkill: (id: number, data: any) => api.put(`/api/admin/skills/${id}`, data),
  deleteSkill: (id: number) => api.delete(`/api/admin/skills/${id}`),
  getActiveSkills: (category?: string) => api.get('/api/skills/active', { params: { category: category || 'content' } }),

  // 图表风格管理（独立于技能）
  getDiagramStyles: () => api.get('/api/admin/diagram-styles'),
  createDiagramStyle: (data: { name: string; description?: string; config_json: string; guidance?: string; is_active?: boolean; sort_order?: number }) =>
    api.post('/api/admin/diagram-styles', data),
  updateDiagramStyle: (id: number, data: { name?: string; description?: string; config_json?: string; guidance?: string; is_active?: boolean; sort_order?: number }) =>
    api.put(`/api/admin/diagram-styles/${id}`, data),
  deleteDiagramStyle: (id: number) => api.delete(`/api/admin/diagram-styles/${id}`),
};
